import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getUserFullName } from "@/utils/formatDisplayName";
import { jobsService } from "@/api/services/jobs.service";
import { quotesService } from "@/api/services/quotes.service";
import { messagesService, parseCreateThreadError, } from "@/api/services/messages.service";
import { useMessagesSignalR } from "@/hooks/useMessagesSignalR";
import { ChatWindow, ComplaintModal, NewMessageModal, QuotesList, JobsList, EmptyState, } from "./components";
import { parseUtcIso } from "@/utils/dateTime";
import { useToast } from "@/components/common";
import styles from "./NotificationsPage.module.css";
function getClientUnreadCount(thread) {
    if (thread.lastMessageByRole === "Client")
        return 0;
    return thread.unreadCount ?? 0;
}
function sortByLastMessage(items) {
    return [...items].sort((a, b) => {
        const aHasChat = a.lastMessageTime != null;
        const bHasChat = b.lastMessageTime != null;
        if (aHasChat && !bHasChat)
            return -1;
        if (!aHasChat && bHasChat)
            return 1;
        if (aHasChat && bHasChat) {
            return b.lastMessageTime.getTime() - a.lastMessageTime.getTime();
        }
        return 0;
    });
}
/**
 * Client notifications: thread and message APIs use only the authenticated user's contact id.
 * Backend enforces that contacts can only access their own threads (403 for other contactIds).
 */
export const NotificationsPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { info: showInfo, error: showError } = useToast();
    // Tab and selection state
    const [activeTab, setActiveTab] = useState("contact");
    const [selectedThread, setSelectedThread] = useState(null);
    const [isNewMessageModalOpen, setIsNewMessageModalOpen] = useState(false);
    const [complaintModalJobId, setComplaintModalJobId] = useState(null);
    // Data state
    const [jobs, setJobs] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [threads, setThreads] = useState([]);
    const [messages, setMessages] = useState([]);
    // Loading states
    const [loadingData, setLoadingData] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);
    // Tab-level unread counts for badges
    const contactUnread = threads.find((t) => t.type === "contact")?.unreadCount ?? 0;
    const quoteUnread = quotes.reduce((sum, q) => sum + (q.unreadCount ?? 0), 0);
    const jobUnread = jobs.reduce((sum, j) => sum + (j.unreadCount ?? 0), 0);
    // Sort jobs and quotes: chatted items by lastMessageTime desc, then unchat
    const sortedJobs = useMemo(() => sortByLastMessage(jobs), [jobs]);
    const sortedQuotes = useMemo(() => sortByLastMessage(quotes), [quotes]);
    // Derived state for support thread (always available in contact tab)
    const supportThread = useMemo(() => ({
        id: "support-main",
        type: "contact",
        title: "The Defit Group",
        subtitle: "DEFIT Staff",
        unreadCount: threads.find((t) => t.type === "contact")?.unreadCount || 0,
        isVerified: true,
    }), [threads]);
    // Fetch jobs, quotes, and threads together so we can use quoteNumber/jobNumber for thread titles
    const fetchAllData = useCallback(async () => {
        if (!user?.id)
            return;
        setLoadingData(true);
        try {
            const [jobsResponse, quotesResponse, quoteThreadsResult, jobThreadsResult] = await Promise.all([
                jobsService.getJobsByContact(user.id),
                quotesService.getQuotesByContact(user.id),
                messagesService.getThreadsByContact(user.id, { type: "quote" }),
                messagesService.getThreadsByContact(user.id, { type: "job" }),
            ]);
            // Transform jobs and quotes
            const jobItems = jobsResponse.data.map((job) => {
                const j = job;
                const hasComplaint = !!(j.complaintnotes ??
                    j.complaintNotes ??
                    j.complaintdate ??
                    j.complaintDate);
                return {
                    id: job.id,
                    jobNumber: job.jobNumber,
                    imageUrl: job.imageUrl || undefined,
                    unreadCount: 0,
                    hasComplaint,
                };
            });
            setJobs(jobItems);
            const EXCLUDED_STATUSES = [
                "draft",
                "inprogress",
                "cancelled",
            ];
            const quoteItems = quotesResponse.data
                .filter((quote) => {
                const hasExcludedStatus = EXCLUDED_STATUSES.includes(quote.status);
                const hasQuoteRequestId = !!quote.quoteRequestId;
                return !hasExcludedStatus || hasQuoteRequestId;
            })
                .map((quote) => ({
                id: quote.id,
                quoteNumber: quote.quoteNumber,
                imageUrl: quote.imageUrl || undefined,
                unreadCount: 0,
            }));
            setQuotes(quoteItems);
            // Normalize API response
            const toThreadList = (response) => {
                const d = response?.data;
                if (Array.isArray(d))
                    return d;
                if (typeof d === "object" &&
                    d !== null &&
                    "data" in d &&
                    Array.isArray(d.data)) {
                    return d.data;
                }
                return [];
            };
            const quoteList = toThreadList(quoteThreadsResult);
            const jobList = toThreadList(jobThreadsResult);
            const allThreads = [];
            // Process quote threads - use quoteNumber from quotes (skip excluded quotes)
            const includedQuoteIds = new Set(quoteItems.map((q) => q.id));
            quoteList.forEach((thread) => {
                const quoteId = thread.quoteId || thread.relatedEntityId;
                if (!quoteId || !includedQuoteIds.has(quoteId))
                    return;
                const quote = quoteItems.find((q) => q.id === quoteId);
                const displayNumber = quote?.quoteNumber ?? quoteId;
                allThreads.push({
                    id: thread.id,
                    type: "quotes",
                    title: `Quote #${displayNumber}`,
                    quoteId: quoteId,
                    unreadCount: getClientUnreadCount(thread),
                    lastMessageTime: thread.lastMessageAt
                        ? parseUtcIso(thread.lastMessageAt)
                        : undefined,
                });
            });
            // Process job threads - use jobNumber from jobs
            jobList.forEach((thread) => {
                const jobId = thread.jobId || thread.relatedEntityId;
                const job = jobItems.find((j) => j.id === jobId);
                const displayNumber = job?.jobNumber ?? jobId;
                allThreads.push({
                    id: thread.id,
                    type: "jobs",
                    title: `Job #${displayNumber}`,
                    jobId: jobId,
                    unreadCount: getClientUnreadCount(thread),
                    lastMessageTime: thread.lastMessageAt
                        ? parseUtcIso(thread.lastMessageAt)
                        : undefined,
                });
            });
            setThreads((prev) => {
                const contactThread = prev.find((t) => t.type === "contact");
                return contactThread
                    ? [contactThread, ...allThreads]
                    : allThreads;
            });
            setJobs((prev) => prev.map((job) => {
                const thread = allThreads.find((t) => t.type === "jobs" && t.jobId === job.id);
                return {
                    ...job,
                    unreadCount: thread?.unreadCount || 0,
                    lastMessageTime: thread?.lastMessageTime,
                    threadId: thread?.id,
                };
            }));
            setQuotes((prev) => prev.map((quote) => {
                const thread = allThreads.find((t) => t.type === "quotes" && t.quoteId === quote.id);
                return {
                    ...quote,
                    unreadCount: thread?.unreadCount || 0,
                    lastMessageTime: thread?.lastMessageTime,
                    threadId: thread?.id,
                };
            }));
        }
        catch (error) {
            console.error("Error fetching data:", error);
        }
        finally {
            setLoadingData(false);
        }
    }, [user?.id]);
    // Fetch contact thread on mount so The Defit Group unread badge shows before user clicks
    const fetchContactThreadOnMount = useCallback(async () => {
        if (!user?.id)
            return;
        try {
            const result = await messagesService.getThreadByGeneral(user.id);
            if (result?.thread) {
                const contactThread = {
                    id: result.thread.id,
                    type: "contact",
                    title: "The Defit Group",
                    subtitle: "DEFIT Staff",
                    unreadCount: getClientUnreadCount(result.thread),
                    isVerified: true,
                    lastMessageTime: result.thread.lastMessageAt
                        ? parseUtcIso(result.thread.lastMessageAt)
                        : undefined,
                };
                setThreads((prev) => {
                    const rest = prev.filter((t) => t.type !== "contact");
                    return [contactThread, ...rest];
                });
            }
        }
        catch {
            // 404 or error - contact thread may not exist yet
        }
    }, [user?.id]);
    // Refresh threads (e.g. after sending message) - uses current jobs/quotes for display numbers
    const fetchThreads = useCallback(async (currentJobs, currentQuotes) => {
        if (!user?.id)
            return;
        try {
            const [quoteThreadsResult, jobThreadsResult] = await Promise.all([
                messagesService.getThreadsByContact(user.id, { type: "quote" }),
                messagesService.getThreadsByContact(user.id, { type: "job" }),
            ]);
            const toThreadList = (response) => {
                const d = response?.data;
                if (Array.isArray(d))
                    return d;
                if (typeof d === "object" &&
                    d !== null &&
                    "data" in d &&
                    Array.isArray(d.data)) {
                    return d.data;
                }
                return [];
            };
            const quoteList = toThreadList(quoteThreadsResult);
            const jobList = toThreadList(jobThreadsResult);
            const allThreads = [];
            quoteList.forEach((thread) => {
                const quoteId = thread.quoteId || thread.relatedEntityId;
                const quote = currentQuotes.find((q) => q.id === quoteId);
                const displayNumber = quote?.quoteNumber ?? quoteId;
                allThreads.push({
                    id: thread.id,
                    type: "quotes",
                    title: `Quote #${displayNumber}`,
                    quoteId: quoteId,
                    unreadCount: getClientUnreadCount(thread),
                    lastMessageTime: thread.lastMessageAt
                        ? parseUtcIso(thread.lastMessageAt)
                        : undefined,
                });
            });
            jobList.forEach((thread) => {
                const jobId = thread.jobId || thread.relatedEntityId;
                const job = currentJobs.find((j) => j.id === jobId);
                const displayNumber = job?.jobNumber ?? jobId;
                allThreads.push({
                    id: thread.id,
                    type: "jobs",
                    title: `Job #${displayNumber}`,
                    jobId: jobId,
                    unreadCount: getClientUnreadCount(thread),
                    lastMessageTime: thread.lastMessageAt
                        ? parseUtcIso(thread.lastMessageAt)
                        : undefined,
                });
            });
            setThreads((prev) => {
                const contactThread = prev.find((t) => t.type === "contact");
                return contactThread
                    ? [contactThread, ...allThreads]
                    : allThreads;
            });
            setJobs((prev) => prev.map((job) => {
                const thread = allThreads.find((t) => t.type === "jobs" && t.jobId === job.id);
                return {
                    ...job,
                    unreadCount: thread?.unreadCount || 0,
                    lastMessageTime: thread?.lastMessageTime,
                    threadId: thread?.id,
                };
            }));
            setQuotes((prev) => prev.map((quote) => {
                const thread = allThreads.find((t) => t.type === "quotes" && t.quoteId === quote.id);
                return {
                    ...quote,
                    unreadCount: thread?.unreadCount || 0,
                    lastMessageTime: thread?.lastMessageTime,
                    threadId: thread?.id,
                };
            }));
        }
        catch (error) {
            console.error("Error fetching threads:", error);
        }
    }, [user?.id]);
    // Fetch messages for a specific thread
    const fetchMessages = useCallback(async (threadId) => {
        try {
            const response = await messagesService.getMessages(threadId);
            const raw = response.data;
            const list = Array.isArray(raw)
                ? raw
                : raw &&
                    typeof raw === "object" &&
                    "data" in raw &&
                    Array.isArray(raw.data)
                    ? raw.data
                    : [];
            const transformedMessages = list.map((msg) => ({
                id: msg.id,
                threadId: msg.threadId,
                content: msg.content,
                senderId: msg.senderId,
                senderName: msg.senderName,
                senderType: msg.senderType,
                timestamp: parseUtcIso(msg.createdAt),
                isRead: msg.isRead,
            }));
            setMessages(transformedMessages);
            // Mark thread as read
            if (transformedMessages.some((m) => !m.isRead && m.senderType === "staff")) {
                await messagesService.markAsRead(threadId);
                setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, unreadCount: 0 } : t)));
            }
        }
        catch (error) {
            console.error("Error fetching messages:", error);
            setMessages([]);
        }
    }, []);
    // Load quote conversation by contact+quote when no thread is in the list yet (one conversation per contact per quote)
    const loadQuoteConversation = useCallback(async (quoteId, quoteNumber) => {
        if (!user?.id)
            return;
        try {
            let result = await messagesService.getThreadByQuote(quoteId, user.id);
            if (!result) {
                try {
                    const createRes = await messagesService.createThread({
                        contactId: user.id,
                        type: "QUOTE",
                        quoteId,
                    });
                    const thread = createRes.data;
                    if (thread?.id) {
                        result = {
                            thread: thread,
                            messages: [],
                        };
                    }
                }
                catch (createErr) {
                    const { message } = parseCreateThreadError(createErr);
                    showError(message);
                }
            }
            if (result) {
                const transformed = result.messages.map((msg) => ({
                    id: msg.id,
                    threadId: msg.threadId,
                    content: msg.content,
                    senderId: msg.senderId,
                    senderName: msg.senderName,
                    senderType: msg.senderType,
                    timestamp: parseUtcIso(msg.createdAt),
                    isRead: msg.isRead,
                }));
                setMessages(transformed);
                const chatThread = {
                    id: result.thread.id,
                    type: "quotes",
                    title: `Quote #${quoteNumber ?? result.thread.quoteId ?? quoteId}`,
                    quoteId: result.thread.quoteId ?? quoteId,
                    unreadCount: getClientUnreadCount(result.thread),
                    lastMessageTime: result.thread.lastMessageAt
                        ? parseUtcIso(result.thread.lastMessageAt)
                        : undefined,
                };
                setThreads((prev) => {
                    const rest = prev.filter((t) => !(t.type === "quotes" && t.quoteId === quoteId));
                    return [...rest, chatThread];
                });
                if (transformed.some((m) => !m.isRead && m.senderType === "staff")) {
                    await messagesService.markAsRead(result.thread.id);
                    setThreads((prev) => prev.map((t) => t.id === result.thread.id ? { ...t, unreadCount: 0 } : t));
                }
            }
            else {
                setMessages([]);
            }
        }
        catch (error) {
            console.error("Error loading quote conversation:", error);
            setMessages([]);
        }
    }, [user?.id, showError]);
    // Load general conversation by contact (single general thread per contact)
    const loadGeneralConversation = useCallback(async () => {
        if (!user?.id)
            return;
        try {
            let result = await messagesService.getThreadByGeneral(user.id);
            if (!result) {
                try {
                    const createRes = await messagesService.createThread({
                        contactId: user.id,
                        type: "GENERAL",
                    });
                    const thread = createRes.data;
                    if (thread?.id) {
                        result = {
                            thread: thread,
                            messages: [],
                        };
                    }
                }
                catch (createErr) {
                    const { message } = parseCreateThreadError(createErr);
                    showError(message);
                }
            }
            if (result) {
                const transformed = result.messages.map((msg) => ({
                    id: msg.id,
                    threadId: msg.threadId,
                    content: msg.content,
                    senderId: msg.senderId,
                    senderName: msg.senderName,
                    senderType: msg.senderType,
                    timestamp: parseUtcIso(msg.createdAt),
                    isRead: msg.isRead,
                }));
                setMessages(transformed);
                const chatThread = {
                    id: result.thread.id,
                    type: "contact",
                    title: "The Defit Group",
                    subtitle: "DEFIT Staff",
                    unreadCount: getClientUnreadCount(result.thread),
                    isVerified: true,
                    lastMessageTime: result.thread.lastMessageAt
                        ? parseUtcIso(result.thread.lastMessageAt)
                        : undefined,
                };
                setThreads((prev) => {
                    const rest = prev.filter((t) => t.type !== "contact");
                    return [...rest, chatThread];
                });
                if (transformed.some((m) => !m.isRead && m.senderType === "staff")) {
                    await messagesService.markAsRead(result.thread.id);
                    setThreads((prev) => prev.map((t) => t.id === result.thread.id ? { ...t, unreadCount: 0 } : t));
                }
            }
            else {
                setMessages([]);
            }
        }
        catch (error) {
            console.error("Error loading general conversation:", error);
            setMessages([]);
        }
    }, [user?.id, showError]);
    // Load job conversation by jobId + assigned contact (one conversation per job and assigned client)
    const loadJobConversation = useCallback(async (jobId, jobNumber) => {
        if (!user?.id)
            return;
        try {
            let result = await messagesService.getThreadByJob(jobId, user.id);
            if (!result) {
                try {
                    const createRes = await messagesService.createThread({
                        contactId: user.id,
                        type: "JOB",
                        jobId,
                    });
                    const thread = createRes.data;
                    if (thread?.id) {
                        result = {
                            thread: thread,
                            messages: [],
                        };
                    }
                }
                catch (createErr) {
                    const { message } = parseCreateThreadError(createErr);
                    showError(message);
                }
            }
            if (result) {
                const transformed = result.messages.map((msg) => ({
                    id: msg.id,
                    threadId: msg.threadId,
                    content: msg.content,
                    senderId: msg.senderId,
                    senderName: msg.senderName,
                    senderType: msg.senderType,
                    timestamp: parseUtcIso(msg.createdAt),
                    isRead: msg.isRead,
                }));
                setMessages(transformed);
                const chatThread = {
                    id: result.thread.id,
                    type: "jobs",
                    title: `Job #${jobNumber ?? result.thread.jobId ?? jobId}`,
                    jobId: result.thread.jobId ?? jobId,
                    unreadCount: getClientUnreadCount(result.thread),
                    lastMessageTime: result.thread.lastMessageAt
                        ? parseUtcIso(result.thread.lastMessageAt)
                        : undefined,
                };
                setThreads((prev) => {
                    const rest = prev.filter((t) => !(t.type === "jobs" && t.jobId === jobId));
                    return [...rest, chatThread];
                });
                if (transformed.some((m) => !m.isRead && m.senderType === "staff")) {
                    await messagesService.markAsRead(result.thread.id);
                    setThreads((prev) => prev.map((t) => t.id === result.thread.id ? { ...t, unreadCount: 0 } : t));
                }
            }
            else {
                setMessages([]);
            }
        }
        catch (error) {
            console.error("Error loading job conversation:", error);
            setMessages([]);
        }
    }, [user?.id, showError]);
    // Initial data fetch
    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);
    // Fetch contact thread on mount so The Defit Group badge shows always
    useEffect(() => {
        fetchContactThreadOnMount();
    }, [fetchContactThreadOnMount]);
    // Handle quoteId and jobId query parameters - switch to tab and select conversation
    useEffect(() => {
        const quoteId = searchParams.get("quoteId");
        if (quoteId && quotes.length > 0) {
            const quoteExists = quotes.some((q) => q.id === quoteId);
            if (quoteExists) {
                setActiveTab("quotes");
                setSelectedThread(quoteId);
                searchParams.delete("quoteId");
                setSearchParams(searchParams, { replace: true });
                return;
            }
        }
        const jobId = searchParams.get("jobId");
        if (jobId && jobs.length > 0) {
            const jobExists = jobs.some((j) => j.id === jobId);
            if (jobExists) {
                setActiveTab("jobs");
                setSelectedThread(jobId);
                searchParams.delete("jobId");
                setSearchParams(searchParams, { replace: true });
            }
        }
    }, [quotes, jobs, threads, searchParams, setSearchParams]);
    // Fetch messages when thread is selected
    useEffect(() => {
        if (selectedThread && selectedThread !== "support-main") {
            // Check if selectedThread is a quoteId/jobId (starts with quote- or job-) or an actual threadId
            // If it's a quoteId/jobId, try to find the existing thread first
            if (activeTab === "quotes" && quotes.length > 0) {
                const quote = quotes.find((q) => q.id === selectedThread);
                if (quote) {
                    const existingThread = threads.find((t) => t.type === "quotes" && t.quoteId === quote.id);
                    if (existingThread) {
                        fetchMessages(existingThread.id);
                    }
                    else {
                        loadQuoteConversation(quote.id, quote.quoteNumber);
                    }
                }
                else {
                    // Not a quote ID, treat as thread ID
                    fetchMessages(selectedThread);
                }
            }
            else if (activeTab === "jobs" && jobs.length > 0) {
                const job = jobs.find((j) => j.id === selectedThread);
                if (job) {
                    // Find existing thread for this job by matching jobId
                    const existingThread = threads.find((t) => t.type === "jobs" && t.jobId === job.id);
                    if (existingThread) {
                        fetchMessages(existingThread.id);
                    }
                    else {
                        loadJobConversation(job.id, job.jobNumber);
                    }
                }
                else {
                    fetchMessages(selectedThread);
                }
            }
            else {
                // Fallback: treat as thread ID
                fetchMessages(selectedThread);
            }
        }
        else if (selectedThread === "support-main") {
            const contactThread = threads.find((t) => t.type === "contact");
            if (contactThread) {
                fetchMessages(contactThread.id);
            }
            else {
                loadGeneralConversation();
            }
        }
        else {
            setMessages([]);
        }
    }, [
        selectedThread,
        threads,
        fetchMessages,
        loadGeneralConversation,
        loadQuoteConversation,
        loadJobConversation,
        activeTab,
        quotes,
        jobs,
    ]);
    // Backend thread ID for the currently viewed conversation (Contact, Job, or Quote).
    // When we just created a thread, selectedThread is set to the new thread id before threads list
    // is refetched, so we use selectedThread as currentThreadId when it looks like a thread id (GUID).
    const isLikelyThreadId = (s) => /^[0-9a-f-]{36}$/i.test(s);
    const currentThreadId = useMemo(() => {
        if (selectedThread === "support-main") {
            return threads.find((t) => t.type === "contact")?.id ?? null;
        }
        if (activeTab === "jobs" && selectedThread) {
            const thread = threads.find((t) => t.type === "jobs" &&
                (t.jobId === selectedThread || t.id === selectedThread));
            return (thread?.id ?? (isLikelyThreadId(selectedThread) ? selectedThread : null));
        }
        if (activeTab === "quotes" && selectedThread) {
            const thread = threads.find((t) => t.type === "quotes" &&
                (t.quoteId === selectedThread || t.id === selectedThread));
            return (thread?.id ?? (isLikelyThreadId(selectedThread) ? selectedThread : null));
        }
        return null;
    }, [activeTab, selectedThread, threads]);
    // Real-time: when a new message is pushed for this thread, refetch and show toast (unless sender is current user)
    useMessagesSignalR(currentThreadId, {
        onNewMessage: (payload) => {
            fetchMessages(payload.threadId);
            if (payload.message.senderId !== user?.id) {
                showInfo("New message received");
            }
        },
        onConnectionError: () => {
            showError("Real-time updates unavailable");
        },
    });
    // Get the current thread info
    const getCurrentThreadInfo = useCallback(() => {
        if (selectedThread === "support-main") {
            return supportThread;
        }
        // Check if it's a job or quote selection (from the lists)
        if (activeTab === "jobs") {
            const job = jobs.find((j) => j.id === selectedThread);
            if (job) {
                const existingThread = threads.find((t) => t.type === "jobs" && t.jobId === job.id);
                return (existingThread || {
                    id: `job-${job.id}`,
                    type: "jobs",
                    title: `Job #${job.jobNumber}`,
                    unreadCount: 0,
                });
            }
        }
        if (activeTab === "quotes") {
            const quote = quotes.find((q) => q.id === selectedThread);
            if (quote) {
                const existingThread = threads.find((t) => t.type === "quotes" && t.quoteId === quote.id);
                return (existingThread || {
                    id: `quote-${quote.id}`,
                    type: "quotes",
                    title: `Quote #${quote.quoteNumber}`,
                    unreadCount: 0,
                });
            }
        }
        return threads.find((t) => t.id === selectedThread);
    }, [selectedThread, activeTab, jobs, quotes, threads, supportThread]);
    // Handle sending a message
    const handleSendMessage = async (content) => {
        if (!selectedThread || !content.trim())
            return;
        setSendingMessage(true);
        // Create optimistic message
        const optimisticMessage = {
            id: `temp-${Date.now()}`,
            threadId: selectedThread,
            content: content.trim(),
            senderId: user?.id || "client",
            senderName: "You",
            senderType: "client",
            timestamp: new Date(),
            isRead: true,
        };
        setMessages((prev) => [...prev, optimisticMessage]);
        try {
            // Determine the message type and related entity
            let apiType = "contact";
            let relatedEntityId;
            let threadId;
            if (activeTab === "contact" || selectedThread === "support-main") {
                apiType = "contact";
                const contactThread = threads.find((t) => t.type === "contact");
                threadId = contactThread?.id;
            }
            else if (activeTab === "jobs") {
                apiType = "job";
                // First check if selectedThread is already a threadId
                const existingThreadById = threads.find((t) => t.id === selectedThread);
                if (existingThreadById && existingThreadById.type === "jobs") {
                    threadId = existingThreadById.id;
                    relatedEntityId = existingThreadById.jobId;
                }
                else {
                    // Otherwise, treat as jobId and find thread
                    const job = jobs.find((j) => j.id === selectedThread);
                    if (job) {
                        relatedEntityId = job.id;
                        const existingThread = threads.find((t) => t.type === "jobs" && t.jobId === job.id);
                        threadId = existingThread?.id;
                    }
                    else {
                        // Fallback: treat as threadId
                        threadId = selectedThread;
                    }
                }
            }
            else if (activeTab === "quotes") {
                apiType = "quote";
                // First check if selectedThread is already a threadId
                const existingThreadById = threads.find((t) => t.id === selectedThread);
                if (existingThreadById && existingThreadById.type === "quotes") {
                    threadId = existingThreadById.id;
                    relatedEntityId = existingThreadById.quoteId;
                }
                else {
                    // Otherwise, treat as quoteId and find thread
                    const quote = quotes.find((q) => q.id === selectedThread);
                    if (quote) {
                        relatedEntityId = quote.id;
                        const existingThread = threads.find((t) => t.type === "quotes" && t.quoteId === quote.id);
                        threadId = existingThread?.id;
                    }
                    else {
                        // Fallback: treat as threadId
                        threadId = selectedThread;
                    }
                }
            }
            const response = await messagesService.sendMessage({
                threadId,
                type: apiType,
                threadType: apiType === "contact" ? "general" : undefined,
                content: content.trim(),
                relatedEntityId,
                ...(apiType === "quote" &&
                    relatedEntityId && { quoteId: relatedEntityId }),
                ...(apiType === "job" &&
                    relatedEntityId && { jobId: relatedEntityId }),
                contactId: !threadId && user?.id ? user.id : undefined,
                senderId: user?.id,
                senderName: getUserFullName(user, "You"),
            });
            // If a new thread was created, add to state and fetch messages
            if (!threadId && response.data.threadId) {
                const newThreadId = response.data.threadId;
                if (apiType === "contact") {
                    const chatThread = {
                        id: newThreadId,
                        type: "contact",
                        title: "The Defit Group",
                        subtitle: "DEFIT Staff",
                        unreadCount: 0,
                        isVerified: true,
                    };
                    setThreads((prev) => {
                        const rest = prev.filter((t) => t.type !== "contact");
                        return [chatThread, ...rest];
                    });
                    setSelectedThread("support-main");
                }
                else if (apiType === "quote" && relatedEntityId) {
                    const quote = quotes.find((q) => q.id === relatedEntityId);
                    const chatThread = {
                        id: newThreadId,
                        type: "quotes",
                        title: `Quote #${quote?.quoteNumber ?? relatedEntityId}`,
                        quoteId: relatedEntityId,
                        unreadCount: 0,
                    };
                    setThreads((prev) => {
                        const rest = prev.filter((t) => !(t.type === "quotes" && t.quoteId === relatedEntityId));
                        return [...rest, chatThread];
                    });
                    setSelectedThread(relatedEntityId);
                }
                else if (apiType === "job" && relatedEntityId) {
                    const job = jobs.find((j) => j.id === relatedEntityId);
                    const chatThread = {
                        id: newThreadId,
                        type: "jobs",
                        title: `Job #${job?.jobNumber ?? relatedEntityId}`,
                        jobId: relatedEntityId,
                        unreadCount: 0,
                    };
                    setThreads((prev) => {
                        const rest = prev.filter((t) => !(t.type === "jobs" && t.jobId === relatedEntityId));
                        return [...rest, chatThread];
                    });
                    setSelectedThread(relatedEntityId);
                }
                await fetchMessages(newThreadId);
            }
            else if (threadId) {
                // Replace optimistic message with real one for existing thread
                setMessages((prev) => prev.map((m) => m.id === optimisticMessage.id
                    ? {
                        id: response.data.id,
                        threadId: response.data.threadId,
                        content: response.data.content,
                        senderId: response.data.senderId,
                        senderName: response.data.senderName,
                        senderType: response.data.senderType,
                        timestamp: parseUtcIso(response.data.createdAt),
                        isRead: response.data.isRead,
                    }
                    : m));
            }
            // Refresh threads to update counts
            await fetchThreads(jobs, quotes);
        }
        catch (error) {
            console.error("Error sending message:", error);
            // Remove optimistic message on error
            setMessages((prev) => prev.filter((m) => m.id !== optimisticMessage.id));
        }
        finally {
            setSendingMessage(false);
        }
    };
    // Handle new message from modal
    const handleSendNewMessage = async (content, type, relatedEntityId) => {
        if (!content.trim())
            return;
        try {
            const apiType = type === "quotes" ? "quote" : type === "jobs" ? "job" : "contact";
            const response = await messagesService.sendMessage({
                type: apiType,
                threadType: apiType === "contact" ? "general" : undefined,
                content: content.trim(),
                relatedEntityId,
                ...(apiType === "quote" &&
                    relatedEntityId && { quoteId: relatedEntityId }),
                ...(apiType === "job" &&
                    relatedEntityId && { jobId: relatedEntityId }),
                contactId: user?.id || undefined,
                senderId: user?.id,
                senderName: getUserFullName(user, "You"),
            });
            if (type === "contact" && response.data.threadId) {
                const chatThread = {
                    id: response.data.threadId,
                    type: "contact",
                    title: "The Defit Group",
                    subtitle: "DEFIT Staff",
                    unreadCount: 0,
                    isVerified: true,
                };
                setThreads((prev) => {
                    const rest = prev.filter((t) => t.type !== "contact");
                    return [chatThread, ...rest];
                });
            }
            await fetchThreads(jobs, quotes);
            if (type === "contact") {
                setActiveTab("contact");
                setSelectedThread("support-main");
            }
            else if (relatedEntityId) {
                // Switch to the appropriate tab and select the entity
                setActiveTab(type);
                setSelectedThread(relatedEntityId);
            }
            setIsNewMessageModalOpen(false);
        }
        catch (error) {
            console.error("Error sending new message:", error);
        }
    };
    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSelectedThread(tab === "contact" ? "support-main" : null);
        setMessages([]);
    };
    // Handle job/quote selection
    const handleSelectJob = (jobId) => {
        setSelectedThread(jobId);
    };
    const handleSelectQuote = (quoteId) => {
        setSelectedThread(quoteId);
    };
    const handleJobChatClick = (jobId) => {
        setSelectedThread(jobId);
    };
    const handleQuoteChatClick = (quoteId) => {
        setSelectedThread(quoteId);
    };
    const handleAddQuote = () => {
        navigate("/client/quotes/new");
    };
    // Determine current thread info for chat window
    const currentThreadInfo = getCurrentThreadInfo();
    const hasMessages = messages.length > 0;
    const showChat = selectedThread !== null && (hasMessages || activeTab !== "contact");
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.sidebar, children: [_jsxs("div", { className: styles.sidebarHeader, children: [_jsxs("div", { className: styles.tabs, children: [_jsxs("button", { className: `${styles.tab} ${activeTab === "contact" ? styles.tabActive : ""}`, onClick: () => handleTabChange("contact"), children: [_jsx("span", { children: "Contact DEFIT" }), contactUnread > 0 && (_jsx("span", { className: styles.tabUnreadBadge, children: contactUnread > 99 ? "99+" : contactUnread }))] }), _jsxs("button", { className: `${styles.tab} ${activeTab === "quotes" ? styles.tabActive : ""}`, onClick: () => handleTabChange("quotes"), children: [_jsx("span", { children: "Quote Updates" }), quoteUnread > 0 && (_jsx("span", { className: styles.tabUnreadBadge, children: quoteUnread > 99 ? "99+" : quoteUnread }))] }), _jsxs("button", { className: `${styles.tab} ${activeTab === "jobs" ? styles.tabActive : ""}`, onClick: () => handleTabChange("jobs"), children: [_jsx("span", { children: "Job Updates" }), jobUnread > 0 && (_jsx("span", { className: styles.tabUnreadBadge, children: jobUnread > 99 ? "99+" : jobUnread }))] })] }), _jsxs("button", { className: styles.newMessageBtn, onClick: () => setIsNewMessageModalOpen(true), children: [_jsx("span", { children: "+" }), _jsx("span", { children: "New Message" })] })] }), _jsxs("div", { className: styles.chatList, children: [activeTab === "contact" && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.sectionTitle, children: "Support" }), _jsxs("div", { className: `${styles.chatItem} ${selectedThread === "support-main" ? styles.chatItemActive : ""}`, onClick: () => setSelectedThread("support-main"), children: [_jsxs("div", { className: styles.chatAvatar, children: [_jsx("span", { children: "D" }), supportThread.isVerified && (_jsx("div", { className: styles.verifiedBadge, children: _jsx("svg", { className: styles.checkIcon, viewBox: "0 0 20 20", fill: "currentColor", children: _jsx("path", { fillRule: "evenodd", d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z", clipRule: "evenodd" }) }) }))] }), _jsx("div", { className: styles.chatInfo, children: _jsxs("div", { className: styles.chatHeader, children: [_jsxs("div", { children: [_jsx("div", { className: styles.chatTitle, children: supportThread.title }), supportThread.subtitle && (_jsx("div", { className: styles.chatSubtitle, children: supportThread.subtitle }))] }), (supportThread.unreadCount ?? 0) > 0 && (_jsx("div", { className: styles.unreadBadge, children: supportThread.unreadCount }))] }) })] })] })), activeTab === "quotes" && (_jsx(QuotesList, { quotes: sortedQuotes, selectedQuote: selectedThread, onSelectQuote: handleSelectQuote, onChatClick: handleQuoteChatClick, onAddQuote: handleAddQuote, loading: loadingData })), activeTab === "jobs" && (_jsx(JobsList, { jobs: sortedJobs, selectedJob: selectedThread, onSelectJob: handleSelectJob, onChatClick: handleJobChatClick, loading: loadingData }))] })] }), _jsx("div", { className: styles.mainContent, children: showChat && currentThreadInfo ? (_jsx(ChatWindow, { threadId: currentThreadInfo.id, threadTitle: currentThreadInfo.title || "The Defit Group", messages: messages, onSendMessage: handleSendMessage, isOnline: true, isVerified: currentThreadInfo.isVerified || activeTab === "contact", currentUserId: user?.id, currentUserName: user?.name || "You", sending: sendingMessage, headerActions: activeTab === "jobs" && selectedThread ? (_jsx("button", { type: "button", className: styles.complaintButton, onClick: () => setComplaintModalJobId(selectedThread), children: jobs.find((j) => j.id === selectedThread)?.hasComplaint
                            ? "Edit Complaint"
                            : "Complaint" })) : undefined })) : (_jsx(EmptyState, { type: activeTab, onNewMessage: () => setIsNewMessageModalOpen(true) })) }), _jsx(ComplaintModal, { isOpen: complaintModalJobId !== null, onClose: () => setComplaintModalJobId(null), jobId: complaintModalJobId, onSaved: () => showInfo("Complaint Sent") }), _jsx(NewMessageModal, { isOpen: isNewMessageModalOpen, onClose: () => setIsNewMessageModalOpen(false), activeTab: activeTab, onSend: handleSendNewMessage, jobs: jobs, quotes: quotes })] }));
};
